import { useRef, useState } from "react";
import { ArrowDown, ArrowUpRight, ChevronLeft, ChevronRight, Expand, Instagram } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { archivePhotos, photoSrcSet, tour } from "../lib/tour";
import { useScrollFadeIn } from "../hooks/useScrollFadeIn";

const filters = ["All photos", "2026", "2025"] as const;

export function Gallery() {
  const [filter, setFilter] = useState<(typeof filters)[number]>("All photos");
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const photoTrigger = useRef<HTMLButtonElement | null>(null);
  const { ref, isVisible } = useScrollFadeIn(0.05);
  const filteredPhotos = archivePhotos.filter((photo) => filter === "All photos" || String(photo.year) === filter);
  const visiblePhotos = expanded ? filteredPhotos : filteredPhotos.slice(0, 6);
  const selected = selectedIndex === null ? null : filteredPhotos[selectedIndex];

  const changePhoto = (direction: number) => {
    setSelectedIndex((index) => index === null ? null : (index + direction + filteredPhotos.length) % filteredPhotos.length);
  };

  return (
    <section id="photos" className="section shell gallery-section" aria-labelledby="photos-title">
      <div ref={ref} className={`section-reveal ${isVisible ? "visible" : ""}`}>
        <div className="section-heading">
          <div>
            <p className="eyebrow"><span>03</span> / On the road</p>
            <h2 id="photos-title">From the<br /><em>front row.</em></h2>
          </div>
          <div className="section-intro">
            <p>A few loud memories from previous seasons.</p>
            <a className="text-link" href={tour.instagramUrl} target="_blank" rel="noopener noreferrer">
              <Instagram aria-hidden="true" /> Follow along <ArrowUpRight aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="lineup-toolbar">
          <div className="filter-group" role="group" aria-label="Filter archive photos">
            {filters.map((value) => (
              <button
                type="button"
                className="filter-button"
                key={value}
                aria-pressed={filter === value}
                aria-controls="archive-photos"
                onClick={() => { setFilter(value); setExpanded(false); }}
              >
                {value}
              </button>
            ))}
          </div>
          <p className="result-count" role="status">{filteredPhotos.length} photos / From the archive</p>
        </div>

        <Dialog open={selectedIndex !== null} onOpenChange={(open) => { if (!open) setSelectedIndex(null); }}>
          <div id="archive-photos" className="photo-grid">
            {visiblePhotos.map((photo, index) => (
              <button
                type="button"
                className="photo-card"
                key={photo.id}
                aria-label={`View photo: ${photo.caption}, ${photo.date}`}
                aria-haspopup="dialog"
                onClick={(event) => { photoTrigger.current = event.currentTarget; setSelectedIndex(index); }}
              >
                <span className="photo-card-image">
                  <img
                    src={photo.src}
                    srcSet={photoSrcSet(photo)}
                    sizes="(max-width: 640px) calc((100vw - 3rem) / 2), (max-width: 1100px) calc((100vw - 5rem) / 3), 340px"
                    alt={photo.alt}
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="photo-expand"><Expand aria-hidden="true" /></span>
                </span>
                <span className="photo-card-caption"><strong>{photo.caption}</strong><span>{photo.date}</span></span>
              </button>
            ))}
          </div>

          {selected && (
            <DialogContent
              className="photo-dialog"
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                photoTrigger.current?.focus({ preventScroll: true });
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                  event.preventDefault();
                  changePhoto(event.key === "ArrowLeft" ? -1 : 1);
                }
              }}
            >
              <div className="photo-viewer-image">
                <img src={selected.src} srcSet={photoSrcSet(selected)} sizes="(max-width: 760px) 100vw, 70vw" alt={selected.alt} />
              </div>
              <div className="photo-viewer-footer">
                <div aria-live="polite" aria-atomic="true">
                  <DialogTitle>{selected.caption}</DialogTitle>
                  <DialogDescription>{selected.date} / Previous House Band lineup</DialogDescription>
                  {selected.sourceUrl && (
                    <a className="photo-source" href={selected.sourceUrl} target="_blank" rel="noopener noreferrer">
                      <Instagram aria-hidden="true" /> Original Instagram post <ArrowUpRight aria-hidden="true" />
                    </a>
                  )}
                  {selected.credit && <p className="photo-credit">Photo: {selected.credit}</p>}
                </div>
                <div className="photo-controls">
                  <button type="button" className="icon-button" aria-label="Previous photo" onClick={() => changePhoto(-1)}><ChevronLeft aria-hidden="true" /></button>
                  <span className="photo-position">{(selectedIndex ?? 0) + 1} / {filteredPhotos.length}</span>
                  <button type="button" className="icon-button" aria-label="Next photo" onClick={() => changePhoto(1)}><ChevronRight aria-hidden="true" /></button>
                </div>
              </div>
            </DialogContent>
          )}
        </Dialog>

        {filteredPhotos.length > 6 && (
          <div className="gallery-more">
            <button
              type="button"
              className="button button-secondary"
              aria-expanded={expanded}
              aria-controls="archive-photos"
              onClick={() => {
                if (expanded) document.getElementById("photos")?.scrollIntoView({ block: "start" });
                setExpanded((value) => !value);
              }}
            >
              {expanded ? "Show fewer photos" : `View all ${filteredPhotos.length} photos`}
              <ArrowDown aria-hidden="true" className={expanded ? "rotated-icon" : ""} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
